import { line } from 'd3-shape';
import { selectAll, select } from 'd3-selection';
import 'd3-transition';
import _ from 'lodash';

import appContext from 'scripts/app-context';

import { hideTooltip } from '../../tooltipHelpers';

import { getChartData, getX, getY } from './chart-base';

const TOOLTIP_HEIGHT = 52;
const TOOLTIP_WIDTH = 190;
// additional top gap for tooltipY position
const TOOLTIP_TOP_GAP = 30;

const LINE_COLORS = {
    playerPoints: '#eb2747',
    secondPlayerPoints: '#1da1f2',
};

const DEFAULT_TOOLTIP_STYLES = {
    position: 'absolute',
    left: `55%`,
    top: `50%`,
    transform: `translate(-50%, -50%)`,
    opacity: '1',
    visibility: 'visible',
};

const isMobile = () =>
    ['small', 'extra-small'].indexOf(
        appContext.model.screen.attr('screenSize'),
    ) > -1;

function addPlayerPaths(svg, points, color, xScale, yScale) {
    if (svg && points && points.length > 0) {
        const playerLine = line()
            .defined((d) => d !== null)
            .x((_, i) => xScale(i))
            .y((d) => {
                return yScale(d);
            });

        const path = svg
            .append('path')
            .datum(points)
            .attr('class', 'line')
            .attr('d', playerLine)
            .style('stroke', color)
            .style('stroke-width', 3)
            .style('fill', 'none');

        const totalLength = path.node().getTotalLength();

        path.attr('stroke-dasharray', totalLength + ' ' + totalLength)
            .attr('stroke-dashoffset', totalLength)
            .transition()
            .duration(1500)
            .attr('stroke-dashoffset', 0);

        addPathDots(svg, points, xScale, yScale, color);
    }
}

function _getTooltipTranslate(tooltipXCoords, chartXCoords) {
    if (tooltipXCoords >= chartXCoords + TOOLTIP_WIDTH) {
        return '-110%';
    }

    if (tooltipXCoords <= chartXCoords + TOOLTIP_WIDTH) {
        return '10%';
    }

    return '10%';
}

function _getTooltipYCoords(tooltipYCoords, svgTopCoords, svgBottomCoords) {
    if (tooltipYCoords - TOOLTIP_HEIGHT <= svgTopCoords + TOOLTIP_TOP_GAP) {
        return svgTopCoords + TOOLTIP_HEIGHT + TOOLTIP_TOP_GAP;
    }

    if (tooltipYCoords + TOOLTIP_HEIGHT >= svgBottomCoords) {
        return svgBottomCoords - TOOLTIP_HEIGHT;
    }

    return tooltipYCoords;
}

function _getTooltipCoords(dot, element) {
    const { left: tooltipX, top: tooltipY } = dot.getBoundingClientRect();

    const { top: svgTop, bottom: svgBottom } = element
        .find('svg.js-chart-area')
        .get(0)
        .getBoundingClientRect();
    const { left: chartXCoords } = element
        .closest('.js-chart-container')
        .get(0)
        .getBoundingClientRect();

    const translateX = _getTooltipTranslate(tooltipX, chartXCoords);
    const tooltipYCoords = _getTooltipYCoords(tooltipY, svgTop, svgBottom);

    return {
        tooltipXCoords: tooltipX,
        tooltipYCoords,
        translateX,
    };
}

function calculateRadius(d, radius = 3) {
    if (d.y === null) {
        return 0;
    }

    return radius;
}

function addPathDots(svg, points, xScale, yScale, color) {
    const path = svg
        .selectAll('dot')
        .data(points.map((p, i) => ({ x: i, y: p })))
        .enter()
        .append('circle')
        .attr('class', (d) => `s-graph-dot s-graph-dot-${d.x}`)
        .attr('cx', (d) => xScale(d.x))
        .attr('cy', (d) => yScale(d.y))
        .attr('r', (d) => calculateRadius(d))
        .style('fill', color)
        .style('stroke', color);

    path.attr('r', 0)
        .transition()
        .delay(1400)
        .duration(200)
        .attr('r', (d) => calculateRadius(d, 5))
        .transition()
        .duration(200)
        .attr('r', (d) => calculateRadius(d));
}

function registerListeners(dots, element, tooltip, changeTooltipId) {
    const handleShowTooltip = createShowHandler(
        element,
        tooltip,
        changeTooltipId,
    );
    const handleHideTooltip = () => hideTooltip(tooltip);

    if (isMobile()) {
        dots.on('click', handleShowTooltip);
        element.off('click');
        element.on('click', function(event) {
            const isClickOutside = dots
                .filter(function() {
                    return this === event.target;
                })
                .empty();

            if (isClickOutside) {
                handleHideTooltip();
            }
        });

        if (tooltip) {
            tooltip.off();
            tooltip.on('click', handleHideTooltip);
        }
    } else {
        dots.on('mouseover', handleShowTooltip).on(
            'mouseout',
            handleHideTooltip,
        );
    }

    handleHideTooltip();
}

const createShowHandler = function(element, tooltip, changeTooltipId) {
    return function(data) {
        const {
            tooltipXCoords,
            tooltipYCoords,
            translateX,
        } = _getTooltipCoords(this, element);

        const { x, y } = data;
        changeTooltipId(x, y);

        if (!Boolean(tooltip)) {
            return;
        }

        if (isMobile()) {
            tooltip.css(DEFAULT_TOOLTIP_STYLES);
        } else {
            tooltip.css({
                left: `${tooltipXCoords}px`,
                top: `${tooltipYCoords}px`,
                transform: `translate(${translateX}, -50%)`,
                opacity: '1',
                visibility: 'visible',
            });
        }
    };
};

function checkDotsCrossing(points, element) {
    const {
        playerPoints,
        secondPlayerPoints,
        playerPointsHover,
        secondPlayerPointsHover,
    } = points;
    playerPoints.forEach((__, index) => {
        if (
            _.get(playerPoints, index) &&
            _.get(secondPlayerPoints, index) &&
            playerPoints[index].toFixed(2) ===
                secondPlayerPoints[index].toFixed(2)
        ) {
            if (playerPointsHover[index] && secondPlayerPointsHover[index]) {
                const firstPlayerPrioritized =
                    Number(playerPointsHover[index].position) <
                    Number(secondPlayerPointsHover[index].position);

                const dots = element.find(`.s-graph-dot-${index}`);
                if (firstPlayerPrioritized) {
                    select(dots[0]).raise();
                } else {
                    select(dots[1]).raise();
                }
            }
        }
    });
}

export default function drawChart(
    chartContainer,
    tooltip,
    data,
    changeTooltipId,
) {
    const { svg, width, height } = getChartData(chartContainer);

    const points = {
        playerPoints: data.points.playerPoints,
        secondPlayerPoints: data.points.secondPlayerPoints,
    };
    const max = data.yMax;
    const xScale = getX(width);
    const yScale = getY(height, max);
    _.forIn(points, (value, key) =>
        addPlayerPaths(
            svg,
            value,
            _.get(LINE_COLORS, key, LINE_COLORS.playerPoints),
            xScale,
            yScale,
        ),
    );

    checkDotsCrossing(data.points, chartContainer);
    const dots = selectAll(chartContainer.find('.s-graph-dot').get());
    registerListeners(dots, chartContainer, tooltip, changeTooltipId);
}
